/**
 * Workflow Analytics Dashboard
 * 
 * Admin dashboard for monitoring workflow performance, identifying bottlenecks,
 * and tracking team workload.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  TrendingUp,
  Users,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Activity,
  Workflow,
  Calendar,
  ArrowUp,
  ArrowDown,
  Loader2,
  RefreshCw,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import * as workflowApi from '../api/workflows';

// Utility for class names
const cn = (...classes) => classes.filter(Boolean).join(' ');

// Metric Card Component
function MetricCard({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
            {subtitle && (
              <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
            )}
            {trend !== undefined && (
              <div className={cn(
                "flex items-center gap-1 mt-2 text-sm",
                trend >= 0 ? "text-green-600" : "text-red-600"
              )}>
                {trend >= 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                <span>{Math.abs(trend)}% vs last period</span>
              </div>
            )}
          </div>
          <div className={cn("p-3 rounded-lg", colorClasses[color])}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Template Performance Card
function TemplatePerformanceCard({ templates }) {
  if (!templates || templates.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Workflow className="w-5 h-5 text-purple-600" />
            Template Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-center py-8">No workflow templates yet</p>
        </CardContent>
      </Card>
    );
  }

  const maxInstances = Math.max(...templates.map(t => t.total_instances), 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Workflow className="w-5 h-5 text-purple-600" />
          Template Performance
        </CardTitle>
        <CardDescription>Workflow usage and completion rates by template</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {templates.slice(0, 5).map((template) => (
            <div key={template.template_id} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "w-3 h-3 rounded-full",
                    template.color === 'blue' ? 'bg-blue-500' :
                    template.color === 'green' ? 'bg-green-500' :
                    template.color === 'purple' ? 'bg-purple-500' :
                    template.color === 'orange' ? 'bg-orange-500' :
                    'bg-gray-500'
                  )} />
                  <span className="font-medium text-sm">{template.template_name}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span>{template.total_instances} workflows</span>
                  <Badge variant="outline" className={cn(
                    template.completion_rate_percent >= 70 ? 'text-green-600' :
                    template.completion_rate_percent >= 40 ? 'text-yellow-600' :
                    'text-red-600'
                  )}>
                    {template.completion_rate_percent}% complete
                  </Badge>
                </div>
              </div>
              <Progress 
                value={(template.total_instances / maxInstances) * 100} 
                className="h-2"
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Bottlenecks Table
function BottlenecksTable({ bottlenecks }) {
  if (!bottlenecks || bottlenecks.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            Bottlenecks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-center py-8">No bottlenecks identified yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-600" />
          Bottlenecks
        </CardTitle>
        <CardDescription>Steps that take the longest to complete</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Step</TableHead>
              <TableHead>Workflow</TableHead>
              <TableHead className="text-right">Avg Time</TableHead>
              <TableHead className="text-right">Completions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bottlenecks.slice(0, 5).map((item, index) => (
              <TableRow key={item.step_id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {index < 3 && (
                      <Badge variant="outline" className={cn(
                        index === 0 ? 'bg-red-50 text-red-600 border-red-200' :
                        index === 1 ? 'bg-orange-50 text-orange-600 border-orange-200' :
                        'bg-yellow-50 text-yellow-600 border-yellow-200'
                      )}>
                        #{index + 1}
                      </Badge>
                    )}
                    {item.step_name}
                  </div>
                </TableCell>
                <TableCell className="text-gray-500">{item.template_name}</TableCell>
                <TableCell className="text-right">
                  <span className={cn(
                    "font-medium",
                    item.avg_completion_hours > 24 ? 'text-red-600' :
                    item.avg_completion_hours > 8 ? 'text-yellow-600' :
                    'text-green-600'
                  )}>
                    {item.avg_completion_hours < 1 
                      ? `${Math.round(item.avg_completion_hours * 60)}m`
                      : `${item.avg_completion_hours}h`
                    }
                  </span>
                </TableCell>
                <TableCell className="text-right">{item.completions}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// User Workload Table
function UserWorkloadTable({ workload }) {
  if (!workload || workload.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Team Workload
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-center py-8">No active tasks assigned</p>
        </CardContent>
      </Card>
    );
  }

  const maxTasks = Math.max(...workload.map(u => u.pending_tasks), 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          Team Workload
        </CardTitle>
        <CardDescription>Task distribution across team members</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {workload.slice(0, 6).map((user) => (
            <div key={user.user_id} className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium text-sm">{user.user_name}</span>
                  <span className="text-gray-400 text-xs ml-2">{user.email}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className={cn(
                    "font-medium",
                    user.pending_tasks > 5 ? 'text-red-600' :
                    user.pending_tasks > 2 ? 'text-yellow-600' :
                    'text-green-600'
                  )}>
                    {user.pending_tasks} pending
                  </span>
                  <span className="text-gray-500">
                    {user.completed_last_7_days} done (7d)
                  </span>
                </div>
              </div>
              <Progress 
                value={(user.pending_tasks / maxTasks) * 100} 
                className="h-2"
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Recent Activity Feed
function RecentActivityFeed({ activities }) {
  const getActionIcon = (action) => {
    switch (action) {
      case 'workflow_started': return <Workflow className="w-4 h-4 text-blue-500" />;
      case 'step_completed': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'workflow_completed': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'step_started': return <Activity className="w-4 h-4 text-blue-500" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Activity className="w-5 h-5 text-green-600" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!activities || activities.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No recent activity</p>
        ) : (
          <div className="space-y-4 max-h-80 overflow-y-auto">
            {activities.map((activity) => (
              <div key={activity.id} className="flex gap-3 items-start">
                <div className="mt-0.5">{getActionIcon(activity.action)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{activity.user_name}</span>
                    {' '}{activity.action.replace(/_/g, ' ')}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{activity.workflow_name}</p>
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  {formatTime(activity.created_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Stale Workflows Alert Card
function StaleWorkflowsCard({ staleWorkflows, isLoading }) {
  if (isLoading) {
    return (
      <Card className="border-yellow-200 bg-yellow-50/50">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-yellow-600" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!staleWorkflows || staleWorkflows.length === 0) {
    return (
      <Card className="border-green-200 bg-green-50/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            No Stale Workflows
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 text-sm">
            All workflows are progressing within their expected timeframes.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-red-200 bg-red-50/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            Stale Workflows
            <Badge variant="destructive" className="ml-2">
              {staleWorkflows.length}
            </Badge>
          </div>
        </CardTitle>
        <CardDescription className="text-red-700/80">
          These workflows haven't been updated within their expected timeframe
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {staleWorkflows.slice(0, 5).map((workflow) => (
            <div 
              key={workflow.id} 
              className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-100"
              data-testid={`stale-workflow-${workflow.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{workflow.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-500">{workflow.template_name}</span>
                  {workflow.current_step && (
                    <>
                      <span className="text-gray-300">•</span>
                      <span className="text-xs text-gray-500">
                        Step: {workflow.current_step}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm font-semibold text-red-600">
                    {workflow.days_stale} days
                  </p>
                  <p className="text-xs text-gray-400">
                    stale
                  </p>
                </div>
                <Link 
                  to={`/workflows?instance=${workflow.id}`}
                  className="text-blue-600 hover:text-blue-700"
                >
                  <ExternalLink className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
        {staleWorkflows.length > 5 && (
          <p className="text-sm text-gray-500 text-center mt-3">
            +{staleWorkflows.length - 5} more stale workflows
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function WorkflowAnalyticsDashboard() {
  const [periodDays, setPeriodDays] = useState('30');

  // Fetch analytics data
  const { data: summaryData, isLoading: loadingSummary, refetch: refetchSummary } = useQuery({
    queryKey: ['workflow-analytics-summary', periodDays],
    queryFn: () => workflowApi.getAnalyticsSummary(parseInt(periodDays)),
  });

  const { data: templatesData, isLoading: loadingTemplates } = useQuery({
    queryKey: ['workflow-analytics-templates', periodDays],
    queryFn: () => workflowApi.getAnalyticsByTemplate(parseInt(periodDays)),
  });

  const { data: bottlenecksData, isLoading: loadingBottlenecks } = useQuery({
    queryKey: ['workflow-analytics-bottlenecks', periodDays],
    queryFn: () => workflowApi.getBottlenecks(parseInt(periodDays)),
  });

  const { data: workloadData, isLoading: loadingWorkload } = useQuery({
    queryKey: ['workflow-analytics-workload'],
    queryFn: () => workflowApi.getUserWorkload(),
  });

  const { data: activityData, isLoading: loadingActivity } = useQuery({
    queryKey: ['workflow-analytics-activity'],
    queryFn: () => workflowApi.getRecentActivity(15),
  });

  const { data: staleData, isLoading: loadingStale } = useQuery({
    queryKey: ['workflow-stale'],
    queryFn: () => workflowApi.getStaleWorkflows(),
  });

  const summary = summaryData?.data || {};
  const templates = templatesData?.data || [];
  const bottlenecks = bottlenecksData?.data || [];
  const workload = workloadData?.data || [];
  const activities = activityData?.data || [];
  const staleWorkflows = staleData?.data || [];

  const isLoading = loadingSummary || loadingTemplates || loadingBottlenecks || loadingWorkload || loadingActivity;

  const handleRefresh = () => {
    refetchSummary();
  };

  return (
    <div className="p-6 space-y-6" data-testid="workflow-analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-blue-600" />
            Workflow Analytics
          </h1>
          <p className="text-gray-500 mt-1">
            Monitor workflow performance and team productivity
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={periodDays} onValueChange={setPeriodDays}>
            <SelectTrigger className="w-[140px]" data-testid="period-selector">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={isLoading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          title="Total Workflows"
          value={summary.total_workflows || 0}
          subtitle={`${summary.active_workflows || 0} active`}
          icon={Workflow}
          color="blue"
        />
        <MetricCard
          title="Completion Rate"
          value={`${summary.completion_rate_percent || 0}%`}
          subtitle={`${summary.completed_workflows || 0} completed`}
          icon={CheckCircle2}
          color="green"
        />
        <MetricCard
          title="Avg Completion Time"
          value={summary.avg_completion_hours < 24 
            ? `${summary.avg_completion_hours || 0}h`
            : `${Math.round((summary.avg_completion_hours || 0) / 24)}d`
          }
          subtitle="Time to complete"
          icon={Clock}
          color="purple"
        />
        <MetricCard
          title="Pending Tasks"
          value={summary.pending_tasks || 0}
          subtitle="Awaiting action"
          icon={AlertTriangle}
          color={summary.pending_tasks > 10 ? 'red' : 'yellow'}
        />
        <MetricCard
          title="Stale Workflows"
          value={staleWorkflows.length}
          subtitle="Need attention"
          icon={AlertCircle}
          color={staleWorkflows.length > 0 ? 'red' : 'green'}
        />
      </div>

      {/* Stale Workflows Alert */}
      {staleWorkflows.length > 0 && (
        <StaleWorkflowsCard staleWorkflows={staleWorkflows} isLoading={loadingStale} />
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Template Performance */}
        <TemplatePerformanceCard templates={templates} />

        {/* Bottlenecks */}
        <BottlenecksTable bottlenecks={bottlenecks} />
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Workload */}
        <UserWorkloadTable workload={workload} />

        {/* Recent Activity */}
        <RecentActivityFeed activities={activities} />
      </div>
    </div>
  );
}
